/**
 * Four email asks that give first (owner, 2026-10-07; the approved mockup is
 * scratch/email-optins/mockup.html): a quiet line where the person already is, a small row after
 * the click, and the address with Turn off once it is done.
 *
 * - learning-note: Learning › Results, "Get a short note when this job learns something new".
 *   Weekly, numbers only, until turned off (one job each).
 * - speed-report: the speed test's result card, "Email me this report". One email, the card's facts.
 * - shortcut-sheet: the F1 sheet, "Email me this sheet". One email.
 * - extra-week: Settings › License, the Add 7 free days form's one line saying tips come with the
 *   extra week. It is the tips sign-up itself (`/events` consent with `source: "extra-week"`).
 *
 * Phone Home sends the emails (its `/api/capturepoint/v6/email-asks` and `/learning-weeks`). The
 * only typed text that leaves the computer is the email and the optional first name; everything
 * else is numbers, code words and the hardware names the speed test already sends.
 *
 * Renderer-safe: no Node, Electron or credential.
 */

import { phoneHomeEmailPattern } from './engagement';

/**
 * ON since Phone Home's email-ask endpoints went live (Phone Home PR #27; owner, 2026-10-07). While
 * false no screen shows an ask, the License form shows no tips line, and main sends nothing. A
 * development run can see them against a local stand-in with `CAPTUREPOINT_DEV_EMAIL_ASKS=preview`
 * (main/email-asks-preview.ts; never a real email).
 */
export const EMAIL_ASKS_ENABLED: boolean = true;

export const emailAskKinds = ['learning-note', 'speed-report', 'shortcut-sheet'] as const;
export type EmailAskKind = (typeof emailAskKinds)[number];
export const isEmailAskKind = (value: unknown): value is EmailAskKind =>
  typeof value === 'string' && (emailAskKinds as readonly string[]).includes(value);

/** The words on screen. They describe the email only (no data-sharing wording: owner rule, 2026-09-27). */
export const emailAskWords: Readonly<
  Record<EmailAskKind, { line: string; button: string; done: string; waiting: string | null }>
> = {
  'learning-note': {
    line: 'Get a short note when this job learns something new',
    button: 'Send me notes',
    done: 'Notes for this job go to',
    waiting: null,
  },
  'speed-report': {
    line: 'Email me this report',
    button: 'Send',
    done: 'Report sent to',
    waiting: 'Report goes to',
  },
  'shortcut-sheet': {
    line: 'Email me this sheet',
    button: 'Send',
    done: 'Sheet sent to',
    waiting: 'Sheet goes to',
  },
};
/** After the address while a one-off email waits to be sent (offline, or Phone Home busy). */
export const EMAIL_ASK_WAITING_TAIL = 'as soon as it can be sent';
export const EMAIL_ASK_ALSO_TIPS = 'Also send me a few tips';
export const EMAIL_ASK_FIRST_NAME = 'First name (optional)';
export const EMAIL_ASK_EMAIL = 'Your email';
export const EMAIL_ASK_TURN_OFF = 'Turn off';
/** D: above Add 7 free days, only while this computer isn't getting tips (mockup, D · Extra week). */
export const EXTRA_WEEK_TIPS_LINE =
  'We’ll send a few tips to help you make the most of your extra week. Unsubscribe anytime.';

/** The speed test card's "What may have slowed it down" lines, as Phone Home's codes (speedTestWhyLines icons). */
export const speedReportWhyCodes = [
  'no-graphics-card',
  'graphics-card-shared',
  'graphics-card-busy',
  'computer-in-use',
] as const;
export type SpeedReportWhyCode = (typeof speedReportWhyCodes)[number];
export const speedReportWhyCode = (
  icon: 'card' | 'apps' | 'processor' | 'person',
): SpeedReportWhyCode =>
  icon === 'card'
    ? 'no-graphics-card'
    : icon === 'apps'
      ? 'graphics-card-shared'
      : icon === 'processor'
        ? 'graphics-card-busy'
        : 'computer-in-use';

/**
 * The speed test's result card, as its email shows it: what the card shows for this computer and
 * no more. Never an app's name: Free graphics memory is only enough, short, or not shown.
 */
export type SpeedReportFacts = {
  testedUtc: string;
  score: number;
  why: SpeedReportWhyCode[];
  speedBoost: 'on' | 'partial' | 'attention' | 'off' | 'unknown';
  /** [] when the check found no card; null when it is not known. */
  graphicsCards: string[] | null;
  processor: string | null;
  memoryGb: number | null;
  /** The job in use's skills; null with no job, or before they are read. */
  skills: { ready: number; total: number } | null;
  /** The training schedule: `start`/`end` as 24-hour "HH:MM", null when it is off. */
  training: { on: boolean; start: string | null; end: string | null } | null;
  freeGraphicsMemory: 'enough' | 'short' | null;
};

export type EmailAskInput = {
  kind: EmailAskKind;
  email: string;
  firstName?: string;
  /** Ticked "Also send me a few tips" (offered only while this computer isn't getting tips). */
  alsoTips?: boolean;
  /** learning-note: the job, and the picture on its card (one of the 100 job icons). */
  jobId?: string;
  jobIcon?: number;
  /** speed-report: the card's facts. */
  report?: SpeedReportFacts;
};
export type EmailAskTurnOffInput = { kind: EmailAskKind; jobId?: string };

/** Sent, or saved to send as soon as it can be (offline, Phone Home busy); or refused, with the friendly words and the exact error. */
export type EmailAskResult =
  { outcome: 'done' | 'waiting' } | { outcome: 'failed'; message: string };

export type EmailAskState = { email: string; waiting: boolean };
export type EmailAsksView = {
  /** False while the switch is off: no ask is shown anywhere. */
  available: boolean;
  /** The first email given anywhere on this computer, and a first name, to fill the row next time. */
  prefill: { email: string; firstName: string };
  /** This computer is getting tips: "Also send me a few tips" is not offered. */
  tipsOn: boolean;
  /** Learning notes on, by job id. */
  learningNotes: Record<string, EmailAskState>;
  /** The speed report sent for one test (its time). */
  speedReport: (EmailAskState & { testedUtc: string }) | null;
  /** The shortcut sheet sent today (this computer's day). */
  shortcutSheet: EmailAskState | null;
};

export const emptyEmailAsksView: EmailAsksView = {
  available: false,
  prefill: { email: '', firstName: '' },
  tipsOn: false,
  learningNotes: {},
  speedReport: null,
  shortcutSheet: null,
};

export const emailAskLimits = Object.freeze({
  email: 254,
  firstName: 100,
  hardwareName: 120,
  graphicsCards: 8,
});
export const emailAskMessages = Object.freeze({
  email: 'Enter a valid email address.',
  firstName: `Keep the first name to ${emailAskLimits.firstName} characters.`,
  invalid: 'That email could not be set up.',
});

const controlCharacters = /[\u0000-\u001f\u007f]/;
const timeOfDay = /^([01]\d|2[0-3]):[0-5]\d$/;
const wholeNumber = (value: unknown, min: number, max: number): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;
const plainText = (value: unknown, max: number): value is string =>
  typeof value === 'string' &&
  value.trim().length > 0 &&
  value.length <= max &&
  !controlCharacters.test(value);

/** An address Phone Home accepts (its own rule), or null. */
export function emailAskAddress(value: unknown): string | null {
  const email = typeof value === 'string' ? value.trim() : '';
  return email && email.length <= emailAskLimits.email && phoneHomeEmailPattern.test(email)
    ? email
    : null;
}

/** The first word of a full name ("Pat Morgan" → "Pat"), for the first-name box. */
export function firstNameOf(name: string | null | undefined): string {
  const first = (name ?? '').trim().split(/\s+/)[0] ?? '';
  return first.length <= emailAskLimits.firstName && !controlCharacters.test(first) ? first : '';
}

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
function speedReportFacts(value: unknown): SpeedReportFacts {
  const facts = value as SpeedReportFacts | null;
  const bad = () => new Error(emailAskMessages.invalid);
  if (!facts || typeof facts !== 'object') throw bad();
  if (typeof facts.testedUtc !== 'string' || !Number.isFinite(Date.parse(facts.testedUtc)))
    throw bad();
  if (!wholeNumber(facts.score, 1, 100)) throw bad();
  if (
    !Array.isArray(facts.why) ||
    facts.why.length > 3 ||
    new Set(facts.why).size !== facts.why.length ||
    facts.why.some((code) => !(speedReportWhyCodes as readonly string[]).includes(code))
  )
    throw bad();
  if (!['on', 'partial', 'attention', 'off', 'unknown'].includes(facts.speedBoost)) throw bad();
  if (
    facts.graphicsCards !== null &&
    (!Array.isArray(facts.graphicsCards) ||
      facts.graphicsCards.length > emailAskLimits.graphicsCards ||
      facts.graphicsCards.some((name) => !plainText(name, emailAskLimits.hardwareName)))
  )
    throw bad();
  if (facts.processor !== null && !plainText(facts.processor, emailAskLimits.hardwareName))
    throw bad();
  if (facts.memoryGb !== null && !wholeNumber(facts.memoryGb, 1, 16_384)) throw bad();
  if (
    facts.skills !== null &&
    (!facts.skills ||
      !wholeNumber(facts.skills.ready, 0, 100_000) ||
      !wholeNumber(facts.skills.total, 0, 100_000) ||
      facts.skills.ready > facts.skills.total)
  )
    throw bad();
  if (
    facts.training !== null &&
    (!facts.training ||
      typeof facts.training.on !== 'boolean' ||
      (facts.training.on
        ? !timeOfDay.test(String(facts.training.start)) ||
          !timeOfDay.test(String(facts.training.end))
        : facts.training.start !== null || facts.training.end !== null))
  )
    throw bad();
  if (
    facts.freeGraphicsMemory !== null &&
    facts.freeGraphicsMemory !== 'enough' &&
    facts.freeGraphicsMemory !== 'short'
  )
    throw bad();
  // A fresh object: nothing the window added rides along (an app's name, a job's name).
  return {
    testedUtc: new Date(facts.testedUtc).toISOString(),
    score: facts.score,
    why: [...facts.why],
    speedBoost: facts.speedBoost,
    graphicsCards:
      facts.graphicsCards === null ? null : facts.graphicsCards.map((name) => name.trim()),
    processor: facts.processor === null ? null : facts.processor.trim(),
    memoryGb: facts.memoryGb,
    skills: facts.skills === null ? null : { ready: facts.skills.ready, total: facts.skills.total },
    training:
      facts.training === null
        ? null
        : { on: facts.training.on, start: facts.training.start, end: facts.training.end },
    freeGraphicsMemory: facts.freeGraphicsMemory,
  };
}

/** What main accepts from a window, rebuilt field by field. Throws the words the row shows. */
export function validateEmailAskInput(value: unknown): EmailAskInput {
  const input = value as EmailAskInput | null;
  if (!input || typeof input !== 'object' || !isEmailAskKind(input.kind))
    throw new Error(emailAskMessages.invalid);
  const email = emailAskAddress(input.email);
  if (!email) throw new Error(emailAskMessages.email);
  const firstName = typeof input.firstName === 'string' ? input.firstName.trim() : '';
  if (firstName.length > emailAskLimits.firstName || controlCharacters.test(firstName))
    throw new Error(emailAskMessages.firstName);
  const clean: EmailAskInput = {
    kind: input.kind,
    email,
    ...(firstName ? { firstName } : {}),
    ...(input.alsoTips === true ? { alsoTips: true } : {}),
  };
  if (input.kind === 'learning-note') {
    if (!plainText(input.jobId, 200) || !wholeNumber(input.jobIcon, 0, 99))
      throw new Error(emailAskMessages.invalid);
    return { ...clean, jobId: input.jobId, jobIcon: input.jobIcon };
  }
  if (input.kind === 'speed-report') return { ...clean, report: speedReportFacts(input.report) };
  return clean;
}

export function validateEmailAskTurnOff(value: unknown): EmailAskTurnOffInput {
  const input = value as EmailAskTurnOffInput | null;
  if (!input || !isEmailAskKind(input.kind)) throw new Error(emailAskMessages.invalid);
  if (input.kind === 'learning-note') {
    if (!plainText(input.jobId, 200)) throw new Error(emailAskMessages.invalid);
    return { kind: input.kind, jobId: input.jobId };
  }
  return { kind: input.kind };
}
